import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment.local';
import {
  CommentHttpResult,
  CommentsHttpResult,
  PostCommentResponse,
  SubCommentsHttp,
} from '../models/comments.model';
import { ApiHttpResponse } from '@kinomap-dev/ng-ui-lib';

@Injectable({
  providedIn: 'root',
})
export class CommentApi {
  constructor(private readonly http: HttpClient) { }


  // --- 🏃 ACTIVITY COMMENTS ---
  getCommentsActivity(
    activityId: number,
    sessionId: number,
    page: number
  ): Observable<CommentsHttpResult> {
    const params = new HttpParams().set('page', page);
    const url = environment.api.dashboard.commentActivity
      .replace('{activity}', '' + activityId)
      .replace('{session}', '' + sessionId);
    return this.http.get<CommentsHttpResult>(url, { params });
  }

  postCommentActivity(
    activityId: number,
    sessionId: number,
    message: string,
    rootCommentId?: number
  ): Observable<PostCommentResponse> {
    const url = environment.api.dashboard.commentActivity
      .replace('{activity}', '' + activityId)
      .replace('{session}', '' + sessionId);
    let params;
    if (rootCommentId !== undefined) {
      params = new HttpParams().set('root_comment_id', '' + rootCommentId);
    }
    return this.http.post<PostCommentResponse>(
      url,
      {
        body: message,
      },
      { params }
    );
  }

  deleteCommentActivity(
    activityId: number,
    sessionId: number,
    commentId: number
  ): Observable<any> {
    const url = environment.api.dashboard.deleteCommentActivity
      .replace('{activity}', '' + activityId)
      .replace('{session}', '' + sessionId)
      .replace('{comment}', '' + commentId);
    return this.http.delete<any>(url);
  }

  // --- 🗺️  GPS ONLY COMMENTS ---
  getGpsOnlyComments(
    gpsOnlyId: number,
    page: number = 1
  ): Observable<CommentsHttpResult> {
    const params: HttpParams = new HttpParams().set('page', page);
    const url: string = environment.api.gpsOnly.comments.replace(
      '{gpsOnlyId}',
      gpsOnlyId.toString()
    );
    return this.http.get<CommentsHttpResult>(url, { params });
  }

  postCommentGpsOnly(
    gpsOnlyId: string,
    commentBody: string,
    rootCommentId: string | null = null
  ): Observable<any> {
    const url: string = environment.api.gpsOnly.comments.replace(
      '{gpsOnlyId}',
      gpsOnlyId
    );
    const param: HttpParams = new HttpParams().set(
      'root_comment_id',
      rootCommentId
    );
    const body: { body: string } = { body: commentBody };
    return this.http.post<any>(url, body, { params: rootCommentId ? param : undefined });
  }

  deleteCommentGpsOnly(gpsOnlyId: string, commentId: string): Observable<any> {
    const url: string = `${environment.api.gpsOnly.comments.replace('{gpsOnlyId}', gpsOnlyId)}/${commentId}`;
    return this.http.delete<any>(url);
  }

  // --- ▶️ PLAYLIST COMMENTS ---
  getCommentsPlaylist(playlistId: number, page: number): Observable<CommentsHttpResult> {
    const params = new HttpParams().set('page', page);
    const url = environment.api.dashboard.commentPlaylist.replace(
      '{playlist}',
      '' + playlistId
    );
    return this.http.get<CommentsHttpResult>(url, { params });
  }

  postCommentPlaylist(
    playlistId: number,
    message: string,
    rootCommentId?: number
  ): Observable<PostCommentResponse> {
    const url = environment.api.dashboard.commentPlaylist.replace(
      '{playlist}',
      '' + playlistId
    );
    let params;
    if (rootCommentId !== undefined) {
      params = new HttpParams().set('root_comment_id', '' + rootCommentId);
    }
    return this.http.post<PostCommentResponse>(
      url,
      {
        body: message,
      },
      { params }
    );
  }

  deletePlaylist(playlistId: number, commentId: number): Observable<any> {
    const url = environment.api.dashboard.deleteCommentPlaylist
      .replace('{playlist}', '' + playlistId)
      .replace('{comment}', '' + commentId);
    return this.http.delete<any>(url);
  }


  // --- 📹 VIDEO SCENIC COMMENTS ---
  getCommentsVideo(videoId: number, page: number) {
    const params = new HttpParams().set('page', page);
    const url = environment.api.dashboard.commentVideo.replace(
      '{video}',
      '' + videoId
    );
    return this.http.get<CommentsHttpResult>(url, { params });
  }

  postCommentVideo(
    videoId: number,
    message: string,
    rootCommentId?: number
  ): Observable<PostCommentResponse> {
    const url: string = environment.api.dashboard.commentVideo.replace(
      '{video}',
      '' + videoId
    );
    let params: HttpParams = new HttpParams();
    if (rootCommentId !== undefined) {
      params = params.set('root_comment_id', '' + rootCommentId);
    }
    return this.http.post<PostCommentResponse>(
      url,
      {
        body: message,
      },
      { params }
    );
  }

  deleteVideo(videoId: number, commentId: number): Observable<any> {
    const url: string = environment.api.dashboard.deleteCommentVideo
      .replace('{video}', '' + videoId)
      .replace('{comment}', '' + commentId);
    return this.http.delete<any>(url);
  }

  // --- 🏋️‍♀️ COACHING COMMENTS ---
  getCommentsCoaching(coachingId: number, page: number) {
    const params = new HttpParams().set('page', page);
    const url = environment.api.coaching.comments.replace(
      '{coachingId}',
      '' + coachingId
    );
    return this.http.get<CommentsHttpResult>(url, { params });
  }

  getPreviewCommentsCoaching(coachingId: number) {
    const params: HttpParams = new HttpParams().set('page', 1).set('limit', 3);
    const url: string = environment.api.coaching.comments.replace(
      '{coachingId}',
      '' + coachingId
    );
    return this.http
      .get<ApiHttpResponse<Comment[]>>(url, { params })
      .pipe(
        map(
          (commentApiResponse: ApiHttpResponse<Comment[]>) =>
            commentApiResponse.data
        )
      );
  }

  postCommentCoaching(
    coachingId: number,
    message: string,
    rootCommentId?: number
  ): Observable<PostCommentResponse> {
    const url = environment.api.coaching.comments.replace(
      '{coachingId}',
      '' + coachingId
    );
    let params;
    if (rootCommentId !== undefined) {
      params = new HttpParams().set('root_comment_id', '' + rootCommentId);
    }
    return this.http.post<PostCommentResponse>(
      url,
      {
        body: message,
      },
      { params }
    );
  }

  deleteCoaching(coachingId: number, commentId: number): Observable<any> {
    const url = environment.api.coaching.deleteComment
      .replace('{coachingId}', '' + coachingId)
      .replace('{commentId}', '' + commentId);
    return this.http.delete<any>(url);
  }

  getSubComments(rootCommentId: number): Observable<CommentHttpResult[]> {
    const url = environment.api.dashboard.subComments.replace(
      '{commentId}',
      '' + rootCommentId
    );
    return this.http.get<SubCommentsHttp>(url).pipe(map(({ data }) => data));
  }


  // --- 📊 WORKOUT COMMENT ---

  getCommentsWorkout(
    workoutId: number,
    limit: number = 15,
    page: number = 1
  ): Observable<CommentsHttpResult> {
    const url: string = environment.api.workout.comments.replace(
      '{workoutId}',
      workoutId.toString()
    );
    return this.http
      .get<CommentsHttpResult>(url, {
        params: {
          page,
          limit,
        },
      })
  }

  postCommentWorkout(
    workoutId: number,
    message: string,
    rootCommentId?: number
  ): Observable<PostCommentResponse> {
    const url: string = environment.api.workout.comments.replace(
      '{workoutId}',
      workoutId.toString()
    );
    let params: HttpParams;
    if (rootCommentId !== undefined) {
      params = new HttpParams().set('root_comment_id', workoutId.toString());
    }
    return this.http.post<PostCommentResponse>(
      url,
      {
        body: message,
      },
      { params }
    );
  }

  // ---  REPORT COMMENT ---
  reportComment(
    commentId: number,
    comment: string,
    reason: string,
    userId: number,
    trackSectionId: number
  ): Observable<any> {
    const url: string = environment.api.watch.reportComment.replace(
      '{commentId}',
      '' + commentId
    );
    const requestBody = {
      reason: reason,
      comment: comment,
      user_id: userId,
      video_id: trackSectionId,
    };
    return this.http.post(url, requestBody);
  }
}
